import * as XLSX from 'xlsx';

// Canonical column order. Headers must match (case-insensitive, trimmed).
export const TEMPLATE_COLUMNS = [
  'email',
  'department',
  'team',
  'location',
  'level',
  'gender',
  'tenure_years',
] as const;

export type TemplateColumn = (typeof TEMPLATE_COLUMNS)[number];

// Which template columns become band dimensions (everything except email).
const BAND_COLUMNS: TemplateColumn[] = ['department', 'team', 'location', 'level', 'gender', 'tenure_years'];

export type ParsedRow = {
  rowNumber: number; // 1-based, excluding the header row
  email: string;
  bands: { dimension: string; label: string }[];
  errors: string[];
  warnings: string[];
};

export type ParseResult = {
  rows: ParsedRow[];
  unknownColumns: string[];
  headerError: string | null;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeHeader(h: unknown): string {
  return String(h ?? '').trim().toLowerCase().replace(/\s+/g, '_');
}

export function parsePeopleSheet(input: File | string | ArrayBuffer): Promise<ParseResult> {
  return new Promise(async (resolve, reject) => {
    try {
      let wb: XLSX.WorkBook;
      if (typeof input === 'string') {
        wb = XLSX.read(input, { type: 'string' });
      } else if (input instanceof ArrayBuffer) {
        wb = XLSX.read(input, { type: 'array' });
      } else {
        const buf = await input.arrayBuffer();
        wb = XLSX.read(buf, { type: 'array' });
      }
      const sheet = wb.Sheets[wb.SheetNames[0]];
      if (!sheet) {
        resolve({ rows: [], unknownColumns: [], headerError: 'No sheet found in file.' });
        return;
      }
      const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
        header: 1,
        defval: '',
        blankrows: false,
      }) as unknown as unknown[][];
      if (raw.length === 0) {
        resolve({ rows: [], unknownColumns: [], headerError: 'Sheet is empty.' });
        return;
      }
      const headerRow = (raw[0] as unknown[]).map(normalizeHeader);
      const colIndex = new Map<string, number>();
      headerRow.forEach((h, i) => {
        if (h) colIndex.set(h, i);
      });

      if (!colIndex.has('email')) {
        resolve({
          rows: [],
          unknownColumns: [],
          headerError: 'Missing required "email" column in the first row.',
        });
        return;
      }

      const unknownColumns = headerRow.filter((h) => h && !(TEMPLATE_COLUMNS as readonly string[]).includes(h));

      const rows: ParsedRow[] = [];
      for (let i = 1; i < raw.length; i++) {
        const r = raw[i] as unknown[];
        if (!r || r.every((c) => String(c ?? '').trim() === '')) continue;

        const errors: string[] = [];
        const warnings: string[] = [];
        const emailRaw = String(r[colIndex.get('email')!] ?? '').trim();
        const email = emailRaw.toLowerCase();

        if (!email) errors.push('Email is required');
        else if (!EMAIL_RE.test(email)) errors.push('Invalid email format');

        const bands: { dimension: string; label: string }[] = [];
        for (const col of BAND_COLUMNS) {
          if (!colIndex.has(col)) continue;
          const cell = r[colIndex.get(col)!];
          const v = String(cell ?? '').trim();
          if (!v) continue;
          if (col === 'tenure_years') {
            const num = Number(v);
            if (Number.isNaN(num)) {
              warnings.push(`tenure_years "${v}" is not a number, ignored`);
              continue;
            }
            bands.push({ dimension: 'tenure_years', label: String(num) });
          } else {
            bands.push({ dimension: col, label: v });
          }
        }

        rows.push({ rowNumber: i, email, bands, errors, warnings });
      }

      resolve({ rows, unknownColumns, headerError: null });
    } catch (e) {
      reject(e);
    }
  });
}

export function buildTemplateWorkbook(): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  const data = [
    TEMPLATE_COLUMNS as unknown as string[],
    ['jane@example.com', 'Engineering', 'Platform', 'London', 'IC4', 'female', '3'],
    ['john@example.com', 'Sales', 'EMEA', 'Berlin', 'Manager', 'male', '5.5'],
  ];
  const ws = XLSX.utils.aoa_to_sheet(data);
  XLSX.utils.book_append_sheet(wb, ws, 'People');

  const instructions = [
    ['How to use this template'],
    [''],
    ['1. Keep the header row exactly as is. Column order does not matter, but headers must match.'],
    ['2. "email" is required. All other columns are optional — leave empty if unknown.'],
    ['3. "tenure_years" must be a number (decimals OK, e.g. 2.5).'],
    ['4. Re-uploading the same email merges new bands; existing assignments are kept.'],
    ['5. Save as .xlsx or .csv and upload on the People page.'],
  ];
  const wsI = XLSX.utils.aoa_to_sheet(instructions);
  XLSX.utils.book_append_sheet(wb, wsI, 'Instructions');
  return wb;
}

export function downloadTemplate(format: 'xlsx' | 'csv') {
  const wb = buildTemplateWorkbook();
  if (format === 'csv') {
    const csv = XLSX.utils.sheet_to_csv(wb.Sheets['People']);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    triggerDownload(blob, 'people-template.csv');
  } else {
    const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    triggerDownload(blob, 'people-template.xlsx');
  }
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
